import { Button } from "@/components/ui/button";
import { Lock, Star } from "lucide-react";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { Link } from "react-router-dom";

// Optimized images via vite-imagetools - different sizes for mobile/desktop
import heroMobile from "@/assets/easy-taxes-hero-devices.png?w=520&format=webp";
import heroDesktop from "@/assets/easy-taxes-hero-devices.png?w=1240&format=webp";

export const Hero = () => {
  const { trackCTA } = useGoogleAnalytics();

  return (
    <section 
      className="relative pt-16 pb-10 md:pt-24 md:pb-12 lg:pt-28 lg:pb-16 min-h-0 lg:min-h-[80vh] overflow-hidden mt-14 md:mt-16 bg-transparent"
      aria-labelledby="hero-title"
    >
      {/* Subtle halo effect behind content */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] md:w-[800px] h-[500px] md:h-[800px] rounded-full pointer-events-none"
        aria-hidden="true"
        style={{
          background: 'radial-gradient(circle, rgba(220, 228, 222, 0.25) 0%, transparent 70%)'
        }}
      />
      
      <div className="container-custom relative z-10 px-4">
        <div className="grid md:grid-cols-[1fr_1fr] lg:grid-cols-[1fr_1.1fr] gap-4 md:gap-8 lg:gap-10 items-center md:justify-items-center">
          {/* Left Column - Text Content */}
          <div className="flex flex-col justify-center h-full space-y-4 md:space-y-5 animate-fade-in-up text-left">
            {/* Eyebrow */}
            <p className="text-[11px] md:text-xs lg:text-sm tracking-[0.08em] md:tracking-[0.12em] uppercase font-normal"
              style={{ color: 'rgba(11, 59, 50, 0.7)' }}
            >
              Easy Taxes <span className="font-normal" style={{ color: 'rgba(11, 59, 50, 0.5)' }}>par</span> ELYX Finance
            </p>
            
            {/* H1 - LCP element - NO animation delay */}
            <h1 
              id="hero-title"
              className="text-[1.75rem] leading-[1.2] sm:text-[1.85rem] md:text-[1.9rem] lg:text-[2.2rem] xl:text-[2.5rem] font-semibold font-heading tracking-tight"
              style={{ color: '#0A3830' }}
            >
              Les impôts, sans stress. Enfin.
            </h1>
            
            {/* Subtitle - NO animation delay for LCP */}
            <div className="space-y-0">
              <p className="text-sm md:text-base lg:text-lg font-body leading-relaxed"
                style={{ color: 'rgba(11, 59, 50, 0.75)' }}
              >
                Une interface claire, pour savoir où vous en êtes.
              </p>
              <p className="text-sm md:text-base lg:text-lg font-body leading-relaxed"
                style={{ color: 'rgba(11, 59, 50, 0.75)' }}
              >
                Un interlocuteur fiscal dédié.
              </p>
              <p className="text-sm md:text-base lg:text-lg font-body leading-relaxed"
                style={{ color: 'rgba(11, 59, 50, 0.75)' }}
              >
                Une déclaration encadrée, validée avant envoi.
              </p>
            </div>

            {/* CTA Button - Mobile optimized tap target */}
            <div className="pt-2 md:pt-2 flex justify-center md:justify-start">
              <a 
                href="https://app.easy-taxes.ch/auth?tab=signup" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 rounded-xl"
                aria-label="Créer mon espace sécurisé Easy Taxes (ouvre dans un nouvel onglet)"
                onClick={() => trackCTA('cta_click_signup', 'hero_main', 'hero')}
              >
                <Button 
                  variant="cta" 
                  size="lg"
                  className="text-green-primary md:text-white text-sm md:text-base font-semibold tracking-wide px-6 md:px-8 py-4 md:py-6 min-h-[48px] md:min-h-[48px] cta-scroll-animate"
                  style={{ backgroundColor: '#BFA06A' }}
                >
                  <Lock className="w-4 h-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
                  Créer mon espace sécurisé
                </Button>
              </a>
            </div>

            {/* Micro-text under CTA */}
            <p className="text-[11px] md:text-[13px] leading-relaxed"
              style={{ color: 'rgba(11, 59, 50, 0.7)' }}
            >
              ✓ Accès gratuit · <Link to="/tarifs" className="underline hover:text-green-primary/90 transition-colors" aria-label="Voir les tarifs Easy Taxes">Tarif estimatif avant validation</Link> · Sans engagement
            </p>

            {/* Trust ratings - Mobile only */}
            <div className="flex lg:hidden items-center gap-3 pt-1">
              <a href="https://www.google.com/search?q=elyx+finance#mpd=~5002743455710783791/customers/reviews" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" aria-label="Google">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="flex gap-px">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-2.5 h-2.5 text-gold-primary fill-gold-primary" strokeWidth={1.5} />
                  ))}
                </span>
                <span className="text-[10px] font-semibold" style={{ color: 'rgba(10, 56, 48, 0.6)' }}>5/5</span>
              </a>

              <div className="w-px h-3 bg-foreground/15" />

              <a href="https://fr.trustpilot.com/review/elyx-finance.ch" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" aria-label="Trustpilot">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" fill="#00B67A"/>
                </svg>
                <span className="flex gap-px">
                  {[...Array(4)].map((_, i) => (
                    <Star key={i} className="w-2.5 h-2.5 text-gold-primary fill-gold-primary" strokeWidth={1.5} />
                  ))}
                  <svg className="w-2.5 h-2.5" viewBox="0 0 24 24">
                    <defs><linearGradient id="halfStarMob"><stop offset="70%" stopColor="hsl(var(--gold-primary))" /><stop offset="70%" stopColor="transparent" /></linearGradient></defs>
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="url(#halfStarMob)" stroke="hsl(var(--gold-primary))" strokeWidth="1.5" />
                  </svg>
                </span>
                <span className="text-[10px] font-semibold" style={{ color: 'rgba(10, 56, 48, 0.6)' }}>4.7/5</span>
              </a>
            </div>

            {/* Questions block - Hidden on mobile */}
            <div className="hidden lg:block pt-1">
              <a 
                href="https://calendly.com/decouverte-elyx-finance/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-block min-h-[44px] flex items-center justify-start focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 rounded-md"
                aria-label="Réserver un appel découverte gratuit avec un expert (ouvre dans un nouvel onglet)"
                onClick={() => trackCTA('cta_click_calendly', 'hero_calendly', 'hero')}
              >
                <div>
                  <p className="text-[13px] font-medium transition-colors"
                    style={{ color: 'rgba(10, 56, 48, 0.65)' }}
                  >
                    Une question avant de commencer ?
                  </p>
                  <p className="text-[13px] transition-colors group-hover:underline"
                    style={{ color: 'rgba(11, 59, 50, 0.55)' }}
                  >
                    Un conseiller fiscal vous rappelle - entretien offert.
                  </p>
                </div>
              </a>

              {/* Trust ratings */}
              <div className="flex items-center gap-4 mt-3">
                <a href="https://www.google.com/search?q=elyx+finance#mpd=~5002743455710783791/customers/reviews" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:opacity-80 transition-opacity">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-label="Google">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span className="flex gap-px">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-gold-primary fill-gold-primary" strokeWidth={1.5} />
                    ))}
                  </span>
                  <span className="text-[11px] font-semibold" style={{ color: 'rgba(10, 56, 48, 0.7)' }}>5/5</span>
                </a>

                <div className="w-px h-3.5 bg-foreground/15" />

                <a href="https://fr.trustpilot.com/review/elyx-finance.ch" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:opacity-80 transition-opacity">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-label="Trustpilot">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" fill="#00B67A"/>
                  </svg>
                  <span className="flex gap-px">
                    {[...Array(4)].map((_, i) => (
                      <Star key={i} className="w-3 h-3 text-gold-primary fill-gold-primary" strokeWidth={1.5} />
                    ))}
                    <svg className="w-3 h-3" viewBox="0 0 24 24">
                      <defs>
                        <linearGradient id="halfStarHero">
                          <stop offset="70%" stopColor="hsl(var(--gold-primary))" />
                          <stop offset="70%" stopColor="transparent" />
                        </linearGradient>
                      </defs>
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="url(#halfStarHero)" stroke="hsl(var(--gold-primary))" strokeWidth="1.5" />
                    </svg>
                  </span>
                  <span className="text-[11px] font-semibold" style={{ color: 'rgba(10, 56, 48, 0.7)' }}>4.7/5</span>
                </a>
              </div>
            </div>
          </div>

          {/* Mobile image */}
          <div className="flex md:hidden justify-center mt-6">
            <img 
              src={heroMobile} 
              alt="Interface Easy Taxes affichée sur ordinateur portable et smartphone montrant le tableau de bord de déclaration fiscale"
              role="img"
              className="w-full max-w-[260px] h-auto"
              width={260}
              height={203}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              style={{ 
                filter: 'drop-shadow(0 15px 30px rgba(11, 59, 50, 0.12))',
              }}
            />
          </div>

          {/* Desktop version - simple CSS hover effect instead of JS parallax */}
          <div 
            className="hidden md:flex items-center justify-center group relative transition-transform duration-300 ease-out hover:scale-[1.02]"
          >
            {/* Golden glow behind desktop */}
            <div 
              className="absolute inset-0 -z-10"
              aria-hidden="true"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(201, 164, 76, 0.18) 0%, rgba(201, 164, 76, 0.08) 40%, transparent 70%)',
                filter: 'blur(50px)',
                transform: 'scale(1.4) translateY(10px)'
              }}
            />
            <img 
              src={heroDesktop} 
              alt="Interface Easy Taxes affichée sur ordinateur portable et smartphone montrant le tableau de bord de déclaration fiscale"
              role="img"
              width={620}
              height={484}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="w-full max-w-[380px] lg:max-w-[550px] xl:max-w-[620px] h-auto transition-all duration-500 ease-out group-hover:scale-[1.02]"
              style={{ 
                filter: 'drop-shadow(0 30px 60px rgba(11, 59, 50, 0.15)) drop-shadow(0 15px 30px rgba(11, 59, 50, 0.1))',
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};